import { useMemo, useContext, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ColumnDef, Row } from "@tanstack/react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSquarePlus,
  faPen,
  faTrash,
  faArrowRotateLeft,
  faUser,
  faUserTie,
  faBuilding,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import CustomTable from "../../../components/CustomTable/CustomTable";
import { Loading } from "../../../components/Loading/Loading";
import { useGetData } from "../../../hooks/useGetData";
import { useMutationData } from "../../../hooks/useMutationData";
import { EvaluadoInterface } from "../../../interfaces/EvaluadoInterface";
import { EmpresaInterface } from "../../../interfaces/EmpresaInterface";
import { ThemeContext } from "../../../components/ThemeContext/ThemeContext";
import { useBloquesContext } from "../../../context/BloquesContext";
import { BLOQUES } from "../../../constants/bloques";
import { motion } from "framer-motion";
import SkyAngelLogo from "../../../img/SkyAngel.png";

export const EvaluadosListaScreen = () => {
  const { isDarkMode } = useContext(ThemeContext);
  const { verificarBloqueActivo } = useBloquesContext();
  const navigate = useNavigate();
  const location = useLocation();

  // Obtener el parámetro 'tipo' de la URL
  const searchParams = new URLSearchParams(location.search);
  const tipoEvaluado = searchParams.get("tipo") || "todos";

  // Estados para manejar tabs
  const [activeTab, setActiveTab] = useState(tipoEvaluado);

  // Estado para el modal de confirmación
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [evaluadoToAction, setEvaluadoToAction] = useState<{
    id: string;
    action: "delete" | "restore";
  } | null>(null);

  // Estado para búsqueda
  const [searchTerm, setSearchTerm] = useState("");

  // Guardar el tipo de evaluado en localStorage para mantenerlo entre navegaciones
  useEffect(() => {
    if (tipoEvaluado !== "todos") {
      localStorage.setItem("ultimoTipoEvaluado", tipoEvaluado);
    }

    // Sincronizar el tab activo con el parámetro de URL
    setActiveTab(tipoEvaluado);
  }, [tipoEvaluado]);

  const queryEvaluados = useGetData<EvaluadoInterface[]>({
    url: "/evaluado",
    name: ["evaluados"],
  });

  const queryEmpresas = useGetData<EmpresaInterface[]>({
    url: "/empresa?estatus[eq]=1",
    name: ["empresas"],
  });

  const [isDeleting, setIsDeleting] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutateEvaluado = useMutationData({
    url: "/evaluado",
    reload: ["evaluado"],
  });

  const handleConfirmAction = () => {
    if (!evaluadoToAction) return;

    if (evaluadoToAction.action === "delete") {
      setIsDeleting(true);
      mutateEvaluado.mutate(
        { id: evaluadoToAction.id, data: { estatus: 0 }, tipo: "delete" },
        {
          onSuccess: () => {
            setIsDeleting(false);
            queryEvaluados.refetch();
            setShowConfirmModal(false);
          },
          onError: (err) => {
            setError("Ocurrió un error al eliminar el evaluado.");
            setIsDeleting(false);
            setShowConfirmModal(false);
            console.error(err);
          },
        }
      );
    } else {
      setIsRestoring(true);
      mutateEvaluado.mutate(
        { id: evaluadoToAction.id, data: { estatus: 1 }, tipo: "patch" },
        {
          onSuccess: () => {
            setIsRestoring(false);
            queryEvaluados.refetch();
            setShowConfirmModal(false);
          },
          onError: (err) => {
            setError("Ocurrió un error al restaurar el evaluado.");
            setIsRestoring(false);
            setShowConfirmModal(false);
            console.error(err);
          },
        }
      );
    }
  };

  const handleDeleteEvaluado = (id: string) => {
    setEvaluadoToAction({ id, action: "delete" });
    setShowConfirmModal(true);
  };

  const handleRestoreEvaluado = (id: string) => {
    setEvaluadoToAction({ id, action: "restore" });
    setShowConfirmModal(true);
  };

  const empresasMap: { [key: number]: string } = {};
  queryEmpresas.data?.forEach((empresa) => {
    empresasMap[empresa.id] = empresa.nombre;
  });

  const skyAngelId = useMemo(() => {
    if (queryEmpresas.data) {
      const skyAngel = queryEmpresas.data.find(
        (empresa) => empresa.nombre === "SkyAngel"
      );
      return skyAngel?.id;
    }
    return null;
  }, [queryEmpresas.data]);

  const evaluadosInternos = useMemo(() => {
    if (!queryEvaluados.data || !skyAngelId) return [];
    return queryEvaluados.data.filter(
      (evaluado) => Number(evaluado.empresa_id) === skyAngelId
    );
  }, [queryEvaluados.data, skyAngelId]);

  const evaluadosExternos = useMemo(() => {
    if (!queryEvaluados.data || !skyAngelId) return queryEvaluados.data || [];
    return queryEvaluados.data.filter(
      (evaluado) => Number(evaluado.empresa_id) !== skyAngelId
    );
  }, [queryEvaluados.data, skyAngelId]);

  // Filtrar evaluados por término de búsqueda
  const filteredInternos = useMemo(() => {
    if (searchTerm === "") return evaluadosInternos;

    return evaluadosInternos.filter(
      (evaluado) =>
        evaluado.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        evaluado.apellido_paterno
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
    );
  }, [evaluadosInternos, searchTerm, empresasMap]);

  const filteredExternos = useMemo(() => {
    if (searchTerm === "") return evaluadosExternos;

    return evaluadosExternos.filter(
      (evaluado) =>
        evaluado.nombre.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [evaluadosExternos, searchTerm, empresasMap]);

  // Funciones para manejar navegación con el tipo correcto
  const handleNavigateToCrear = () => {
    window.scrollTo(0, 0);
    navigate(`/evaluadosList/crear?tipo=${activeTab}`);
  };

  const handleNavigateToEditar = (uuid: string) => {
    window.scrollTo(0, 0);
    navigate(`/evaluadosList/editar/${uuid}?tipo=${activeTab}`);
  };

  const handleNavigateToEvaluar = (uuid: string) => {
    window.scrollTo(0, 0);
    navigate(`/evaluadosList/evaluar/${uuid}?tipo=${activeTab}`);
  };

  // Cambiar tab
  const handleTabChange = (tipo: string) => {
    navigate(`/evaluadosList?tipo=${tipo}`);
    setActiveTab(tipo);
  };

  const columns = useMemo<ColumnDef<EvaluadoInterface>[]>(
    () => [
      {
        header: "ID",
        accessorKey: "id",
        cell: (info) => info.getValue() as number,
        footer: (props) => props.column.id,
        enableColumnFilter: false,
      },
      {
        accessorKey: "nombre",
        header: () => "Nombre",
        cell: (info) => {
          const evaluado = info.row.original;
          return (
            <div className="flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center mr-2 ${
                  evaluado.estatus === 1
                    ? isDarkMode
                      ? "bg-blue-900"
                      : "bg-blue-800"
                    : isDarkMode
                    ? "bg-red-900"
                    : "bg-red-800"
                }`}
              >
                {Number(evaluado.empresa_id) === skyAngelId ? (
                  <img
                    src={SkyAngelLogo}
                    alt="SkyAngel"
                    className="w-5 h-5 object-contain"
                  />
                ) : (
                  <FontAwesomeIcon icon={faUserTie} className="text-white" />
                )}
              </div>
              <div>
                <div className="font-medium">{info.getValue() as string}</div>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: "apellido_paterno",
        header: () => "Apellido Paterno",
        cell: (info) => {
          const evaluado = info.row.original;
          return (
            <div>
              <div className="font-medium">{evaluado.apellido_paterno}</div>
            </div>
          );
        },
      },
      {
        accessorKey: "apellido_materno",
        header: () => "Apellido Materno",
        cell: (info) => {
          const evaluado = info.row.original;
          return (
            <div>
              <div className="font-medium">{evaluado.apellido_materno}</div>
            </div>
          );
        },
      },
      {
        accessorKey: "correo",
        header: () => "Correo",
        cell: (info) => {
          const evaluado = info.row.original;
          return (
            <div>
              <div className="font-medium">{evaluado.correo}</div>
            </div>
          );
        },
      },
      {
        accessorKey: "empresa_id",
        header: () => "Empresa",
        cell: (info) => {
          const evaluado = info.row.original;
          const empresaId = Number(evaluado.empresa_id);
          const empresaNombre =
            empresasMap[empresaId] || "Empresa no encontrada";

          return (
            <span
              className={`px-3 py-1 rounded-full text-sm ${
                Number(evaluado.empresa_id) === skyAngelId
                  ? isDarkMode
                    ? "bg-indigo-900 text-indigo-200"
                    : "bg-indigo-100 text-indigo-800"
                  : isDarkMode
                  ? "bg-purple-900 text-purple-200"
                  : "bg-purple-100 text-purple-800"
              }`}
            >
              {empresaNombre}
            </span>
          );
        },
        accessorFn: (row) => {
          const empresaId = Number(row.empresa_id);
          return empresasMap[empresaId]?.toLowerCase() || "";
        },
        filterFn: (row, columnId, filterValue) => {
          if (!filterValue) return true;
          const empresaId = Number(row.original.empresa_id);
          const empresaNombre = empresasMap[empresaId]?.toLowerCase() || "";
          return empresaNombre.includes(filterValue.toLowerCase());
        },
      },
      {
        id: "estatus",
        header: "Estatus",
        accessorFn: (row) => (row.estatus === 1 ? "Activo" : "Inactivo"),
        cell: (info) => {
          const estatus = info.getValue() as string;
          const isActive = estatus === "Activo";

          return (
            <span
              className={`font-medium px-3 py-1 rounded-full text-sm ${
                isActive
                  ? isDarkMode
                    ? "bg-green-900 text-green-200"
                    : "bg-green-100 text-green-800"
                  : isDarkMode
                  ? "bg-red-900 text-red-200"
                  : "bg-red-100 text-red-800"
              }`}
            >
              {estatus}
            </span>
          );
        },
      },
      {
        header: "Acciones",
        cell: (info) => (
          <div className="flex space-x-2">
            {verificarBloqueActivo(BLOQUES.VER_EVALUACION) && (
              <button
                title="Detalles del evaluado"
                className={`p-2 rounded-lg transition-all ${
                  isDarkMode
                    ? "bg-green-600 hover:bg-green-700 text-white"
                    : "bg-green-500 hover:bg-green-600 text-white"
                }`}
                onClick={() => handleNavigateToEvaluar(info.row.original.uuid)}
              >
                Evaluaciones
              </button>
            )}
            {verificarBloqueActivo(BLOQUES.EDITAR_EVALUADO) && (
              <button
                title="Editar evaluado"
                className={`p-2 rounded-lg transition-all ${
                  isDarkMode
                    ? "bg-blue-600 hover:bg-blue-700 text-white"
                    : "bg-blue-500 hover:bg-blue-600 text-white"
                }`}
                onClick={() => handleNavigateToEditar(info.row.original.uuid)}
              >
                <FontAwesomeIcon icon={faPen} />
              </button>
            )}
            {info.row.original.estatus === 1 ? (
              <button
                title="Eliminar evaluado"
                className={`p-2 rounded-lg transition-all ${
                  isDarkMode
                    ? "bg-red-600 hover:bg-red-700 text-white"
                    : "bg-red-500 hover:bg-red-600 text-white"
                }`}
                onClick={() => handleDeleteEvaluado(info.row.original.uuid)}
                disabled={isDeleting}
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            ) : (
              <button
                title="Restaurar evaluado"
                className={`p-2 rounded-lg transition-all ${
                  isDarkMode
                    ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                    : "bg-emerald-500 hover:bg-emerald-600 text-white"
                }`}
                onClick={() => handleRestoreEvaluado(info.row.original.uuid)}
                disabled={isRestoring}
              >
                <FontAwesomeIcon icon={faArrowRotateLeft} />
              </button>
            )}
          </div>
        ),
      },
    ],
    [navigate, isDarkMode, isDeleting, isRestoring, empresasMap, skyAngelId]
  );

  if (error) {
    return (
      <div
        className={`min-h-screen p-6 ${
          isDarkMode ? "bg-gray-900" : "bg-gray-50"
        }`}
      >
        <div
          className={`p-4 rounded-lg shadow-md mb-6 ${
            isDarkMode ? "bg-red-900" : "bg-red-100"
          }`}
        >
          <div className="flex items-center text-red-700">
            <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
            <span>{error}</span>
          </div>
        </div>
      </div>
    );
  }

  if (queryEvaluados.isLoading || queryEmpresas.isLoading) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center ${
          isDarkMode ? "bg-gray-900" : "bg-gray-50"
        }`}
      >
        <Loading />
      </div>
    );
  }

  // Estadísticas básicas
  const totalInternos = evaluadosInternos.length;
  const activosInternos = evaluadosInternos.filter(
    (e) => e.estatus === 1
  ).length;
  const totalExternos = evaluadosExternos.length;
  const activosExternos = evaluadosExternos.filter(
    (e) => e.estatus === 1
  ).length;

  return (
    <div
      className={`min-h-screen ${
        isDarkMode ? "bg-gray-900 text-white" : "bg-gray-50 text-gray-800"
      }`}
    >
      {/* Header con título y estadísticas */}
      <div
        className={`w-full ${
          isDarkMode ? "bg-gray-800" : "bg-white"
        } shadow-md`}
      >
        <div className="container mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="flex flex-col items-center justify-center text-center">
              <h1 className="text-3xl font-bold">Gestión de Evaluados</h1>
            </div>

            <div className="flex gap-3">
              {verificarBloqueActivo(BLOQUES.CREAR_EVALUADO) && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`flex items-center gap-2 py-2 px-4 rounded-lg ${
                    isDarkMode
                      ? "bg-blue-600 hover:bg-blue-700 text-white"
                      : "bg-blue-500 hover:bg-blue-600 text-white"
                  } transition-colors duration-300 shadow-md`}
                  onClick={handleNavigateToCrear}
                >
                  <FontAwesomeIcon icon={faSquarePlus} />
                  <span>Crear evaluado</span>
                </motion.button>
              )}
            </div>
          </div>

          {/* Tarjetas de estadísticas */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <div
              className={`p-4 rounded-lg shadow-md ${
                isDarkMode ? "bg-gray-700" : "bg-white"
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p
                    className={`text-sm ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Total Internos
                  </p>
                  <p className="text-2xl font-bold">{totalInternos}</p>
                </div>
                <div
                  className={`p-3 rounded-full ${
                    isDarkMode ? "bg-blue-900" : "bg-blue-800"
                  }`}
                >
                  <img
                    src={SkyAngelLogo}
                    alt="SkyAngel Logo"
                    className={`h-6 ${
                      isDarkMode ? "filter brightness-125" : ""
                    }`}
                  />
                </div>
              </div>
              <p
                className={`text-xs mt-2 ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                {activosInternos} activos (
                {Math.round((activosInternos / totalInternos) * 100) || 0}%)
              </p>
            </div>

            <div
              className={`p-4 rounded-lg shadow-md ${
                isDarkMode ? "bg-gray-700" : "bg-white"
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p
                    className={`text-sm ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Total Externos
                  </p>
                  <p className="text-2xl font-bold">{totalExternos}</p>
                </div>
                <div
                  className={`p-3 rounded-full ${
                    isDarkMode ? "bg-purple-900" : "bg-purple-100"
                  }`}
                >
                  <FontAwesomeIcon
                    icon={faUserTie}
                    className={
                      isDarkMode ? "text-purple-300" : "text-purple-600"
                    }
                    size="lg"
                  />
                </div>
              </div>
              <p
                className={`text-xs mt-2 ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                {activosExternos} activos (
                {Math.round((activosExternos / totalExternos) * 100) || 0}%)
              </p>
            </div>

            <div
              className={`p-4 rounded-lg shadow-md ${
                isDarkMode ? "bg-gray-700" : "bg-white"
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p
                    className={`text-sm ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Total Empresas
                  </p>
                  <p className="text-2xl font-bold">
                    {queryEmpresas.data?.length || 0}
                  </p>
                </div>
                <div
                  className={`p-3 rounded-full ${
                    isDarkMode ? "bg-emerald-900" : "bg-emerald-100"
                  }`}
                >
                  <FontAwesomeIcon
                    icon={faBuilding}
                    className={
                      isDarkMode ? "text-emerald-300" : "text-emerald-600"
                    }
                    size="lg"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contenido principal */}
      <div className="container mx-auto px-6 py-6">
        {/* Mostrar la tabla de Internos solo si tipoEvaluado es "internos" o "todos" */}
        {(activeTab === "internos" || activeTab === "todos") && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="mb-8"
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold flex items-center">
                <img
                  src={SkyAngelLogo}
                  alt="SkyAngel Logo"
                  className={`h-6 mr-2 ${
                    isDarkMode ? "filter brightness-125" : ""
                  }`}
                />
                Evaluados Internos
                <span className="ml-2 text-sm font-normal bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full dark:bg-blue-900 dark:text-blue-300">
                  {filteredInternos.length}
                  {searchTerm && ` de ${evaluadosInternos.length}`}
                </span>
              </h2>
            </div>

            <div
              className={`shadow-md rounded-lg overflow-hidden ${
                isDarkMode ? "bg-gray-800" : "bg-white"
              }`}
            >
              <CustomTable
                {...{
                  data: filteredInternos,
                  columns,
                  getRowProps: (row: Row<EvaluadoInterface>) => ({
                    className: `
                      ${
                        row.original.estatus === 1
                          ? isDarkMode
                            ? "bg-gray-800 hover:bg-gray-700"
                            : "bg-white hover:bg-blue-50"
                          : isDarkMode
                          ? "bg-red-900/20 hover:bg-red-900/30"
                          : "bg-red-50 hover:bg-red-100"
                      }
                      transition-colors
                    `,
                  }),
                }}
              />
            </div>
          </motion.div>
        )}

        {/* Mostrar la tabla de Externos solo si tipoEvaluado es "externos" o "todos" */}
        {(activeTab === "externos" || activeTab === "todos") && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold flex items-center">
                <FontAwesomeIcon
                  icon={faUserTie}
                  className={`mr-2 ${
                    isDarkMode ? "text-purple-400" : "text-purple-500"
                  }`}
                />
                Evaluados Externos
                <span className="ml-2 text-sm font-normal bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full dark:bg-purple-900 dark:text-purple-300">
                  {filteredExternos.length}
                  {searchTerm && ` de ${evaluadosExternos.length}`}
                </span>
              </h2>
            </div>

            <div
              className={`shadow-md rounded-lg overflow-hidden ${
                isDarkMode ? "bg-gray-800" : "bg-white"
              }`}
            >
              <CustomTable
                {...{
                  data: filteredExternos,
                  columns,
                  getRowProps: (row: Row<EvaluadoInterface>) => ({
                    className: `
                      ${
                        row.original.estatus === 1
                          ? isDarkMode
                            ? "bg-gray-800 hover:bg-gray-700"
                            : "bg-white hover:bg-purple-50"
                          : isDarkMode
                          ? "bg-red-900/20 hover:bg-red-900/30"
                          : "bg-red-50 hover:bg-red-100"
                      }
                      transition-colors
                    `,
                  }),
                }}
              />
            </div>
          </motion.div>
        )}
      </div>

      {/* Modal de confirmación */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div
            className={`p-6 rounded-lg shadow-lg max-w-md w-full ${
              isDarkMode ? "bg-gray-800" : "bg-white"
            }`}
          >
            <h3 className="text-xl font-bold mb-4">Confirmar acción</h3>
            <p className="mb-4">
              ¿Estás seguro que deseas{" "}
              {evaluadoToAction?.action === "delete" ? "eliminar" : "restaurar"}{" "}
              este evaluado?
            </p>
            <div className="flex justify-end gap-2">
              <button
                className={`px-4 py-2 rounded ${
                  isDarkMode
                    ? "bg-gray-600 hover:bg-gray-700"
                    : "bg-gray-200 hover:bg-gray-300"
                }`}
                onClick={() => {
                  setShowConfirmModal(false);
                  setEvaluadoToAction(null);
                }}
              >
                Cancelar
              </button>
              <button
                className={`px-4 py-2 rounded text-white ${
                  evaluadoToAction?.action === "delete"
                    ? isDarkMode
                      ? "bg-red-600 hover:bg-red-700"
                      : "bg-red-500 hover:bg-red-600"
                    : isDarkMode
                    ? "bg-green-600 hover:bg-green-700"
                    : "bg-green-500 hover:bg-green-600"
                }`}
                onClick={handleConfirmAction}
                disabled={isDeleting || isRestoring}
              >
                {isDeleting || isRestoring ? (
                  <span className="flex items-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Procesando...
                  </span>
                ) : (
                  <span>Confirmar</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
